import { useEffect } from "react";
import { Stack } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useCreateSubjectSubjectsPostMutation,
  useFindTenantByIdTenantsIdGetQuery,
} from "api";
import { RadioInput, SelectInput, TextInput } from "components/forms";
import { SectionCard } from "components/SectionCard/SectionCard";
import { useNavigate } from "hooks/useNavigate";
import { IZone } from "interfaces/zone.interface";
import {
  toastErrorPositionCenter,
  toastSuccessPositionCenter,
} from "utils/toast.util";

import { SubjectFormSchema, subjectFormSchema } from "./subjectFormSchema";

import { FieldColumns } from "../../components/FieldColumns";
import { FormActions } from "../../components/FormActions";
import { PageHeader } from "../../components/PageHeader";
import { CustomFields } from "../components/CustomFields";

type SubjectCreateFormProps = {
  zoneId: string;
  zones: IZone[];
};

export function SubjectCreateForm({ zoneId, zones }: SubjectCreateFormProps) {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { data: tenantResponse } = useFindTenantByIdTenantsIdGetQuery(
    {
      id: zones[0]?.tenant_id ?? "",
    },
    { skip: zones.length === 0 },
  );

  const [createSubject, { isLoading }] = useCreateSubjectSubjectsPostMutation();

  const { handleSubmit, control, reset, resetField } = useForm({
    resolver: zodResolver(subjectFormSchema),
    defaultValues: {
      zone_id: zoneId,
      name: "",
      custom_fields: {},
    },
  });

  useEffect(() => {
    const defaults = Object.fromEntries(
      tenantResponse?.data?.custom_fields?.map((field) => {
        if (field.id && field.default !== undefined && field.default !== null) {
          return [field.id, field.default];
        }

        return [];
      }) ?? [],
    );

    resetField("custom_fields", defaults);
  }, [resetField, tenantResponse]);

  const onSubmit = async (data: SubjectFormSchema) => {
    try {
      const customFields = { ...data.custom_fields };

      Object.keys(customFields).forEach((key) => {
        const value = customFields[key];
        // Only remove if the value is undefined, null, or empty string
        // Keep "0" and other falsy but valid values like false, 0, etc.
        if (value === undefined || value === null || value === "") {
          delete customFields[key];
        }
      });

      const { data: subjectData } = await createSubject({
        subjectCreate: {
          zone_id: data.zone_id,
          identifier: data.identifier,
          name: data.name,
          sex: data.sex,
          birth_year: data.birth_year,
          custom_fields: customFields as { [key: string]: string },
        },
      });

      if (subjectData?.data) {
        reset();

        toastSuccessPositionCenter(t("Subject created successfully"));
        navigate(`../subjects/${subjectData.data.id}`);
      } else {
        toastErrorPositionCenter(t("Try again"));
      }
    } catch {
      toastErrorPositionCenter(t("Try again"));
    }
  };

  const customFields =
    tenantResponse?.data?.custom_fields?.filter(
      (field) => field.entity === "subject" && field.id,
    ) || [];

  return (
    <Stack gap={2} component={"form"} onSubmit={handleSubmit(onSubmit)}>
      <PageHeader title={t("Create Subject")} />
      <SectionCard title={t("Subject Information")}>
        <FieldColumns>
          <SelectInput
            control={control}
            fieldName={"zone_id"}
            label={t("Zone")}
            disabled
            options={zones.map((zone) => ({
              ...zone,
              label: zone.name,
              disabled: !zone.hasAccess,
              padding: (zone.level - 1) * 2,
            }))}
            placeholder={t("Select a zone")}
          />
          <TextInput
            fieldName={"identifier"}
            control={control}
            label={t("Subject ID")}
            variant={"outlined"}
            required
          />
          <TextInput
            fieldName={"name"}
            control={control}
            label={t("Description (optional)")}
            variant={"outlined"}
          />
          <RadioInput
            fieldName={"sex"}
            control={control}
            label={t("Sex")}
            direction={"row"}
            options={[
              { id: "male", label: t("Male") },
              { id: "female", label: t("Female") },
            ]}
          />
          <TextInput
            fieldName={"birth_year"}
            type={"number"}
            control={control}
            label={t("Birth Year")}
            required
            min={1}
          />
        </FieldColumns>
      </SectionCard>
      <CustomFields customFields={customFields} control={control} />
      <FormActions
        submitText={t("Create")}
        backText={t("Cancel")}
        backHref={"../subjects"}
        isLoading={isLoading}
      />
    </Stack>
  );
}
